/**
 * Shared Typography Presets
 * Single source of truth for font family, sizes, and weights across the application.
 */

import type { SxProps, Theme } from '@mui/material/styles';
import { designTokens } from './tokens';

const { typography: t } = designTokens;

/** Primary application font family */
export const fontFamily = t.fontFamily.primary;

/** Monospace font family for codes, IDs, and numeric data */
export const fontFamilyMono = t.fontFamily.monospace;

/** @deprecated Use `fontFamily` instead */
export const FONT = fontFamily;

/** @deprecated Use `fontFamily` instead */
export const fontHeading = fontFamily;

/** @deprecated Use `fontFamilyMono` instead */
export const fontMono = fontFamilyMono;

/**
 * Reusable typography style objects for `sx` props and styled components.
 * Prefer MUI `<Typography variant="...">` when possible; use these presets for custom layouts.
 */
export const typographyPresets = {
  /** Main page titles (h1 equivalent) */
  heading1: {
    fontFamily,
    fontSize: t.fontSize['3xl'],
    fontWeight: t.fontWeight.bold,
    lineHeight: t.lineHeight.tight,
    letterSpacing: t.letterSpacing.tight,
  },
  /** Large section headings (h2 equivalent) */
  heading2: {
    fontFamily,
    fontSize: t.fontSize['2xl'],
    fontWeight: t.fontWeight.bold,
    lineHeight: t.lineHeight.tight,
    letterSpacing: t.letterSpacing.tight,
  },
  /** Card / panel headings (h3 equivalent) */
  heading3: {
    fontFamily,
    fontSize: t.fontSize.xl,
    fontWeight: t.fontWeight.semibold,
    lineHeight: t.lineHeight.normal,
  },
  /** Compact page header title */
  pageTitle: {
    fontFamily,
    fontSize: '1.375rem',
    fontWeight: t.fontWeight.bold,
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
  },
  /** Subheadings — prominent (h4 equivalent) */
  subheading1: {
    fontFamily,
    fontSize: t.fontSize.lg,
    fontWeight: t.fontWeight.semibold,
    lineHeight: t.lineHeight.normal,
  },
  /** Subheadings — standard (h5 equivalent) */
  subheading2: {
    fontFamily,
    fontSize: t.fontSize.base,
    fontWeight: t.fontWeight.medium,
    lineHeight: t.lineHeight.normal,
  },
  /** Subheadings — compact (h6 equivalent) */
  subheadingSm: {
    fontFamily,
    fontSize: t.fontSize.sm,
    fontWeight: t.fontWeight.medium,
    lineHeight: t.lineHeight.normal,
  },
  /** Default body text */
  body: {
    fontFamily,
    fontSize: t.fontSize.base,
    fontWeight: t.fontWeight.regular,
    lineHeight: t.lineHeight.normal,
  },
  /** Secondary / compact body text */
  bodySm: {
    fontFamily,
    fontSize: t.fontSize.sm,
    fontWeight: t.fontWeight.regular,
    lineHeight: t.lineHeight.normal,
  },
  /** Compact UI text (inputs, breadcrumbs, metadata) */
  uiText: {
    fontFamily,
    fontSize: t.fontSize.ui,
    fontWeight: t.fontWeight.regular,
    lineHeight: t.lineHeight.normal,
  },
  uiTextMedium: {
    fontFamily,
    fontSize: t.fontSize.ui,
    fontWeight: t.fontWeight.medium,
    lineHeight: t.lineHeight.normal,
  },
  uiTextSemibold: {
    fontFamily,
    fontSize: t.fontSize.ui,
    fontWeight: t.fontWeight.semibold,
    lineHeight: t.lineHeight.normal,
  },
  /** Uppercase field / table labels */
  label: {
    fontFamily,
    fontSize: t.fontSize['2xs'],
    fontWeight: t.fontWeight.semibold,
    lineHeight: t.lineHeight.normal,
    textTransform: 'uppercase' as const,
    letterSpacing: t.letterSpacing.wider,
  },
  /** Small helper / badge text */
  caption: {
    fontFamily,
    fontSize: t.fontSize.xs,
    fontWeight: t.fontWeight.regular,
    lineHeight: t.lineHeight.normal,
  },
  captionMedium: {
    fontFamily,
    fontSize: t.fontSize.xs,
    fontWeight: t.fontWeight.medium,
    lineHeight: t.lineHeight.normal,
  },
  captionSemibold: {
    fontFamily,
    fontSize: t.fontSize.xs,
    fontWeight: t.fontWeight.semibold,
    lineHeight: t.lineHeight.normal,
  },
  /** Monospace for codes and reference numbers */
  mono: {
    fontFamily: fontFamilyMono,
    fontSize: t.fontSize.sm,
    fontWeight: t.fontWeight.regular,
    lineHeight: t.lineHeight.normal,
  },
} as const;

export type TypographyPreset = keyof typeof typographyPresets;

// /** Returns a preset as an MUI `sx` value */
// export function typographySx(
//   preset: TypographyPreset,
//   extra?: SxProps<Theme>
// ): SxProps<Theme> {
//   return extra ? [typographyPresets[preset], extra] : typographyPresets[preset];
// }
/** Returns a preset as an MUI `sx` value */
export function typographySx(
  preset: TypographyPreset,
  extra?: SxProps<Theme>
): SxProps<Theme> {
  if (!extra) return typographyPresets[preset];

  const extraArr = Array.isArray(extra) ? extra : [extra];
  return [typographyPresets[preset], ...extraArr];
}
